import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClockIcon, 
  FunnelIcon, 
  CalendarIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const FollowUps = ({ user }) => {
  const navigate = useNavigate();
  const [followUps, setFollowUps] = useState([]);
  const [filteredFollowUps, setFilteredFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  // Helper function to get today's date in datetime-local format
  const getTodayDateTime = () => {
    const now = new Date();
    // Add 1 hour to current time to ensure it's in the future
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
  };
  
  // Enhanced filters
  const [filters, setFilters] = useState({
    action: 'all',
    priority: 'all',
    status: 'all',
    dateRange: 'all',
    specificDate: '',
    searchTerm: ''
  });
  
  // Analytics data
  const [analytics, setAnalytics] = useState({
    total: 0,
    overdue: 0,
    today: 0,
    thisWeek: 0,
    completed: 0
  });
  
  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Calculate analytics
  const calculateAnalytics = useCallback((followUpsData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    
    const overdue = followUpsData.filter(lead => new Date(lead.follow_up_date) < now).length;
    const todayFollowUps = followUpsData.filter(lead => {
      const followUpDate = new Date(lead.follow_up_date);
      return followUpDate >= today && followUpDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
    }).length;
    const thisWeek = followUpsData.filter(lead => new Date(lead.follow_up_date) >= startOfWeek).length;
    
    setAnalytics({
      total: followUpsData.length,
      overdue,
      today: todayFollowUps,
      thisWeek,
      completed: 0 // This would need to be calculated based on completed actions
    });
  }, []);

  // Filter follow-ups based on current filters
  const filterFollowUps = useCallback((followUpsData) => {
    let filtered = [...followUpsData];
    
    // Search filter
    if (filters.searchTerm) {
      const searchTerm = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.name?.toLowerCase().includes(searchTerm) ||
        lead.phone?.toLowerCase().includes(searchTerm) ||
        lead.source?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Action filter
    if (filters.action !== 'all') {
      filtered = filtered.filter(lead => lead.last_action === filters.action);
    }
    
    // Priority filter
    if (filters.priority !== 'all') {
      filtered = filtered.filter(lead => lead.priority === filters.priority);
    }
    
    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(lead => lead.status === filters.status);
    }
    
    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (filters.dateRange) {
        case 'overdue':
          filtered = filtered.filter(lead => new Date(lead.follow_up_date) < now);
          break;
        case 'today':
          filtered = filtered.filter(lead => {
            const followUpDate = new Date(lead.follow_up_date);
            return followUpDate >= today && followUpDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
          });
          break;
        case 'thisWeek':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          filtered = filtered.filter(lead => new Date(lead.follow_up_date) >= startOfWeek);
          break;
        case 'nextWeek':
          const nextWeekStart = new Date(today);
          nextWeekStart.setDate(today.getDate() + 7);
          const nextWeekEnd = new Date(nextWeekStart);
          nextWeekEnd.setDate(nextWeekStart.getDate() + 7);
          filtered = filtered.filter(lead => {
            const followUpDate = new Date(lead.follow_up_date);
            return followUpDate >= nextWeekStart && followUpDate < nextWeekEnd;
          });
          break;
        case 'specificDate':
          if (filters.specificDate) {
            const selectedDate = new Date(filters.specificDate);
            selectedDate.setHours(0, 0, 0, 0);
            const selectedDateEnd = new Date(selectedDate);
            selectedDateEnd.setHours(23, 59, 59, 999);
            
            filtered = filtered.filter(lead => {
              const followUpDate = new Date(lead.follow_up_date);
              return followUpDate >= selectedDate && followUpDate <= selectedDateEnd;
            });
          }
          break;
        default:
          break;
      }
    }
    
    setFilteredFollowUps(filtered);
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const loadFollowUps = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading follow-ups for user:', user.id);
      
      // Build query
      let query = supabase
        .from('leads')
        .select('*')
        .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)
        .not('follow_up_date', 'is', null)
        .order('follow_up_date', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;
      
      console.log('Follow-ups loaded:', data?.length || 0);
      console.log('Sample follow-up data:', data?.[0]);
      console.log('All follow-up data:', data);
      
      // If no user-specific follow-ups, try getting all follow-ups for debugging
      if (!data || data.length === 0) {
        console.log('No user-specific follow-ups found, trying all follow-ups...');
        const { data: allFollowUps, error: allError } = await supabase
          .from('leads')
          .select('*')
          .not('follow_up_date', 'is', null)
          .order('follow_up_date', { ascending: true });
        
        if (!allError && allFollowUps) {
          console.log('All follow-ups in database:', allFollowUps.length);
          console.log('Sample all follow-up:', allFollowUps[0]);
          
          // Use all follow-ups for now (for debugging)
          setFollowUps(allFollowUps);
        } else {
          console.error('Error getting all follow-ups:', allError);
          setFollowUps([]);
        }
      } else {
        setFollowUps(data || []);
      }
    } catch (error) {
      console.error('Error loading follow-ups:', error);
      toast.error(`Error loading follow-ups: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  // Apply filters when followUps or filters change
  useEffect(() => {
    if (followUps.length > 0) {
      calculateAnalytics(followUps);
      filterFollowUps(followUps);
    }
  }, [followUps, filterFollowUps, calculateAnalytics]);

  const setupRealtimeSubscription = useCallback(() => {
    const subscription = supabase
      .channel('followups_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads',
          filter: `assigned_to=eq.${user.id}`
        },
        (payload) => {
          console.log('Follow-ups real-time change (assigned_to):', payload);
          loadFollowUps();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Follow-ups real-time change (user_id):', payload);
          loadFollowUps();
        }
      )
      .subscribe((status) => {
        console.log('Follow-ups real-time subscription status:', status);
      });

    window.followUpsSubscription = subscription;
  }, [user.id, loadFollowUps]);

  const cleanupRealtimeSubscription = () => {
    if (window.followUpsSubscription) {
      console.log('Cleaning up follow-ups real-time subscription');
      supabase.removeChannel(window.followUpsSubscription);
      window.followUpsSubscription = null;
    }
  };

  useEffect(() => {
    if (user) {
      loadFollowUps();
      setupRealtimeSubscription();
    }

    return () => {
      if (user) {
        cleanupRealtimeSubscription();
      }
    };
  }, [user, loadFollowUps, setupRealtimeSubscription]);


  // Bulk actions
  const handleBulkAction = async (action, nextAction, followUpDate = null) => {
    if (selectedLeads.length === 0) {
      toast.error('Please select leads to perform bulk action');
      return;
    }

    try {
      const updates = {
        last_action: action,
        last_action_date: new Date().toISOString()
      };

      if (nextAction === 'Follow Up') {
        if (followUpDate) {
          updates.follow_up_date = new Date(followUpDate).toISOString();
          updates.status = 'follow_up';
        } else {
          toast.error('Follow-up date is required when selecting Follow Up');
          return;
        }
      } else if (nextAction === 'Converted') {
        updates.status = 'converted';
        updates.follow_up_date = null;
      } else if (nextAction === 'Close') {
        updates.status = 'closed';
        updates.follow_up_date = null;
      }

      // Update all selected leads
      const leadIds = selectedLeads.map(lead => lead.id);
      const { error } = await supabase
        .from('leads')
        .update(updates)
        .in('id', leadIds);

      if (error) throw error;

      toast.success(`Bulk action applied to ${selectedLeads.length} leads`);
      setSelectedLeads([]);
      setShowBulkActions(false);
      loadFollowUps();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      toast.error(`Error performing bulk action: ${error.message}`);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === filteredFollowUps.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredFollowUps.map(lead => lead.id));
    }
  };


  const viewLeadDetails = (lead) => {
    navigate(`/leads/${lead.id}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (followUpDate) => {
    return new Date(followUpDate) < new Date();
  };

  // Debug function to test follow-up query
  // eslint-disable-next-line no-unused-vars
  const testFollowUpQuery = async () => {
    try {
      console.log('Testing follow-up query...');
      
      // Test 1: Get all leads with follow-up dates
      const { data: allFollowUps, error: allError } = await supabase
        .from('leads')
        .select('*')
        .not('follow_up_date', 'is', null)
        .order('follow_up_date', { ascending: true });
      
      console.log('All follow-ups in database:', allFollowUps?.length || 0);
      console.log('Sample follow-up:', allFollowUps?.[0]);
      
      // Test 2: Get user-specific follow-ups
      const { data: userFollowUps, error: userError } = await supabase
        .from('leads')
        .select('*')
        .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)
        .not('follow_up_date', 'is', null)
        .order('follow_up_date', { ascending: true });
      
      console.log('User-specific follow-ups:', userFollowUps?.length || 0);
      console.log('User ID:', user.id);
      console.log('Sample user follow-up:', userFollowUps?.[0]);
      
      if (allError) console.error('All follow-ups error:', allError);
      if (userError) console.error('User follow-ups error:', userError);
      
    } catch (error) {
      console.error('Debug query error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Follow Ups</h1>
          <p className="text-gray-600">Manage your scheduled follow-ups and lead actions.</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          {selectedLeads.length > 0 && (
        <button
              onClick={() => setShowBulkActions(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
        >
              Bulk Actions ({selectedLeads.length})
        </button>
          )}
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                <ChartBarIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Follow-ups</p>
              <p className="text-2xl font-semibold text-gray-900">{analytics.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Overdue</p>
              <p className="text-2xl font-semibold text-gray-900">{analytics.overdue}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Today</p>
              <p className="text-2xl font-semibold text-gray-900">{analytics.today}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">This Week</p>
              <p className="text-2xl font-semibold text-gray-900">{analytics.thisWeek}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Completed</p>
              <p className="text-2xl font-semibold text-gray-900">{analytics.completed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search leads..."
                />
                {filters.searchTerm && (
                  <button
                    onClick={() => handleFilterChange('searchTerm', '')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            {/* Action Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Actions</option>
                <option value="Connected">Connected</option>
                <option value="No Answer">No Answer</option>
                <option value="Wrong Number">Wrong Number</option>
                <option value="Do Not Call">Do Not Call</option>
                <option value="Interested">Interested</option>
                <option value="Not Interested">Not Interested</option>
                <option value="Already a Customer">Already a Customer</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="follow_up">Follow Up</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => {
                  handleFilterChange('dateRange', e.target.value);
                  if (e.target.value !== 'specificDate') {
                    handleFilterChange('specificDate', '');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Dates</option>
                <option value="overdue">Overdue</option>
                <option value="today">Today</option>
                <option value="thisWeek">This Week</option>
                <option value="nextWeek">Next Week</option>
                <option value="specificDate">Specific Date</option>
              </select>
            </div>

            {/* Specific Date Input - Show only when "Specific Date" is selected */}
            {filters.dateRange === 'specificDate' && (
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Date
                </label>
                <input
                  type="date"
                  value={filters.specificDate}
                  onChange={(e) => handleFilterChange('specificDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Follow-ups List */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Scheduled Follow-ups ({filteredFollowUps.length})
          </h3>
          {filteredFollowUps.length > 0 && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedLeads.length === filteredFollowUps.length}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500">Select All</span>
            </div>
          )}
        </div>
        <div className="overflow-hidden">
          {filteredFollowUps.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>{filters.searchTerm || filters.action !== 'all' || filters.priority !== 'all' || filters.status !== 'all' || filters.dateRange !== 'all' || (filters.dateRange === 'specificDate' && filters.specificDate) ? 'No follow-ups match your filters.' : 'No follow-ups scheduled.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Select
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S.No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Follow-up Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFollowUps.map((lead, index) => (
                    <tr key={lead.id} className={isOverdue(lead.follow_up_date) ? 'bg-red-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                        <div className="text-sm text-gray-500">{lead.source}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lead.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isOverdue(lead.follow_up_date) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {formatDate(lead.follow_up_date)}
                        </div>
                        {isOverdue(lead.follow_up_date) && (
                          <div className="text-xs text-red-500">Overdue</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          lead.priority === 'high' ? 'bg-red-100 text-red-800' :
                          lead.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {lead.priority || 'low'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.last_action || 'None'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => viewLeadDetails(lead)}
                          className="text-green-600 hover:text-green-900"
                          title="View Lead Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions Modal */}
      {showBulkActions && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Bulk Actions ({selectedLeads.length} leads)
                </h3>
                <button
                  onClick={() => setShowBulkActions(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const action = formData.get('action');
                const nextAction = formData.get('nextAction');
                const followUpDate = formData.get('followUpDate');
                handleBulkAction(action, nextAction, followUpDate);
              }} className="space-y-4">
                <div>
                  <label htmlFor="bulkAction" className="block text-sm font-medium text-gray-700 mb-1">
                    Action *
                  </label>
                  <select
                    id="bulkAction"
                    name="action"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Action</option>
                    <option value="Connected">Connected</option>
                    <option value="No Answer">No Answer</option>
                    <option value="Wrong Number">Wrong Number</option>
                    <option value="Do Not Call">Do Not Call</option>
                    <option value="Interested">Interested</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Already a Customer">Already a Customer</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="bulkNextAction" className="block text-sm font-medium text-gray-700 mb-1">
                    Next Action *
                  </label>
                  <select
                    id="bulkNextAction"
                    name="nextAction"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Next Action</option>
                    <option value="Follow Up">Follow Up</option>
                    <option value="Converted">Converted</option>
                    <option value="Close">Close</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="bulkFollowUpDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Follow-up Date (if Follow Up selected)
                  </label>
                  <input
                    type="datetime-local"
                    id="bulkFollowUpDate"
                    name="followUpDate"
                    defaultValue={getTodayDateTime()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowBulkActions(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Apply to {selectedLeads.length} leads
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUps;
